import { UserCoverageSummary } from '@ampsec/platform-client';
import {
  COLLABORATION,
  ENDPOINT_MANAGEMENT,
  ENDPOINT_SECURITY,
  IDENTITY_AND_ACCESS,
  SECURITY_TRAINING,
  VULNERABILITY_MANAGEMENT,
} from 'Core-utils/constants/constants';
import { assetDomainToTypeMap, getMinWidthForColumn, userDomainToTypeMap } from './AgGridCompliance.config';
import { getEndpointsStatus, getUserConnectorsStatus, getUserRiskReason } from './ToolingComplianceComponentHelper';
import NameCellRenderer, { getNameTooltipExport } from './cellRenderers/NameCellRenderer';
import DepartmentCellRenderer from './cellRenderers/DepartmentCellRenderer';
import AssetsCellRenderer, { getAssetTooltipExport } from './cellRenderers/AssetsCellRenderer';
import EndpointCountCellRenderer from './cellRenderers/EndpointCountCellRenderer';
import DomainCellRenderer, { getDomainTooltipExport } from './cellRenderers/DomainCellRenderer';
import AttributionStatusCellRenderer, { getAttributionTooltipExport } from './cellRenderers/AttributionStatusCellRenderer';
import CollaborationColumnFilter from './CustomFilters/CollaborationColumnFilter';
import DepartmentColumnFilter from './CustomFilters/DepartmentColumnFilter';
import IdentityColumnFilter from './CustomFilters/IdentityColumnFilter';
import TrainingColumnFilter from './CustomFilters/TrainingColumnFilter';
import AssetAssignmentFilter from './CustomFilters/AssetAssignmentFilter';
import VulnerabilityCoverageFilter from './CustomFilters/VulnerabilityCoverageFilter';
import EndpointManagementCoverageFilter from './CustomFilters/EndpointManagementCoverageFilter';
import './styles.scss';

const TEXT_FILTER_PARAMS = {
  buttons: ['reset'],
  debounceMs: 300,
  maxNumConditions: 1,
  suppressAndOrCondition: true,
  filterOptions: ['contains'],
};


const ATTRIBUTION_CONFLICT_LABEL = 'Conflict';
const ATTRIBUTION_NO_CONFLICT_LABEL = 'No Conflict';
const ATTRIBUTION_FILTER_VALUES = [ATTRIBUTION_CONFLICT_LABEL, ATTRIBUTION_NO_CONFLICT_LABEL];

const hasAttributionConflict = (row: UserCoverageSummary): boolean => {
  if (!Array.isArray(row.assets)) {
    return false;
  }

  return row.assets.some((asset: any) =>
    asset?.attributionStatus === 'NEGATIVE' ||
    (Array.isArray(asset?.saasAssets) &&
      asset.saasAssets.some((saasAsset: any) => saasAsset?.attributionStatus === 'NEGATIVE'))
  );
};

/**
 * Wraps a column's tooltip text builder as the `tooltipExport` that {@link buildCsv} expands into an
 * extra "<Header> (Tooltip)" column, so detail that only the cell's hover tooltip renders still
 * reaches the export. It is declared under the colDef's `context` because that is AG Grid's
 * sanctioned annotation slot — it warns about any other unknown colDef property — and it is read
 * only by the CSV export, never by the grid itself.
 */
const withTooltipExport = (valueGetter: (params: any) => string) => ({
  context: { tooltipExport: { valueGetter } },
});

/** The tooltip export of one domain column, using the same domain / cell type the cell renders. */
const domainTooltipExport = (domain: string, isAssetCell: boolean) =>
  withTooltipExport((params: any) => getDomainTooltipExport(params.data, domain, isAssetCell, params.context));

const usersInsightsColumns = [
  {
    field: 'name',
    colId: 'name',
    headerName: 'Name',
    sortable: true,
    minWidth: getMinWidthForColumn('name'),
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    cellRenderer: NameCellRenderer,
    ...withTooltipExport((params: any) => getNameTooltipExport(params.data)),
  },
  {
    field: 'department',
    colId: 'department',
    headerName: 'Department',
    minWidth: getMinWidthForColumn('department'),
    filter: DepartmentColumnFilter,
    sortable: true,
    cellRenderer: DepartmentCellRenderer,
  },
  {
    field: 'identityAndAccess',
    colId: 'identityAndAccess',
    headerName: 'Identity',
    minWidth: getMinWidthForColumn('identityAndAccess'),
    sortable: false,
    filter: IdentityColumnFilter,
    filterParams: { buttons: ['reset'], debounceMs: 300 },
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: IDENTITY_AND_ACCESS,
      domainType: userDomainToTypeMap[IDENTITY_AND_ACCESS],
      isAssetCell: false,
    },
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return row.name?.toLowerCase() === 'unassigned'
        ? 'Unassigned'
        : `Connectors ${getUserConnectorsStatus(row.userAccounts2)}`;
    },
    ...domainTooltipExport(IDENTITY_AND_ACCESS, false),
  },
  {
    field: 'collaboration',
    colId: 'collaboration',
    headerName: 'Collaboration',
    minWidth: getMinWidthForColumn('collaboration'),
    sortable: false,
    filter: CollaborationColumnFilter,
    filterParams: { buttons: ['reset'], debounceMs: 300 },
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: COLLABORATION,
      domainType: userDomainToTypeMap[COLLABORATION],
      isAssetCell: false,
    },
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return row.name?.toLowerCase() === 'unassigned'
        ? ''
        : `Connectors ${getUserConnectorsStatus(row.userAccounts2)}`;
    },
    ...domainTooltipExport(COLLABORATION, false),
  },
  {
    field: 'securityTraining',
    colId: 'securityTraining',
    headerName: 'Training',
    minWidth: getMinWidthForColumn('securityTraining'),
    sortable: false,
    filter: TrainingColumnFilter,
    filterParams: { buttons: ['reset'], debounceMs: 300 },
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: SECURITY_TRAINING,
      domainType: userDomainToTypeMap[SECURITY_TRAINING],
      isAssetCell: false,
    },
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return row.name?.toLowerCase() === 'unassigned'
        ? 'Unassigned'
        : `Connectors ${getUserConnectorsStatus(row.userAccounts2)}`;
    },
    ...domainTooltipExport(SECURITY_TRAINING, false),
  },
  {
    field: 'endpoints',
    colId: 'endpoints',
    headerName: 'Assets',
    minWidth: getMinWidthForColumn('endpoints'),
    sortable: false,
    filter: false,
    cellRenderer: AssetsCellRenderer,
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      if (Array.isArray(row.assets) && row.assets.length > 0) {
        return row.assets.map((a) => a?.displayValue).filter(Boolean).join('; ');
      }
      if (row.name?.toLowerCase() === 'unassigned') {
        return 'Unassigned';
      }
      return '';
    },
    ...withTooltipExport((params: any) => getAssetTooltipExport(params.data, params.context)),
  },
  {
    field: 'attributionStatus',
    colId: 'attributionStatus',
    headerName: 'Attribution Status',
    minWidth: getMinWidthForColumn('attributionStatus'),
    sortable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset'],
      values: ATTRIBUTION_FILTER_VALUES,
    },
    cellRenderer: AttributionStatusCellRenderer,
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return hasAttributionConflict(row) ? ATTRIBUTION_CONFLICT_LABEL : ATTRIBUTION_NO_CONFLICT_LABEL;
    },
    ...withTooltipExport((params: any) => getAttributionTooltipExport(params.data, params.context)),
  },
  {
    field: 'numberOfEndpoints',
    colId: 'numberOfEndpoints',
    headerName: '# Endpoints',
    minWidth: getMinWidthForColumn('numberOfEndpoints'),
    sortable: false,
    filter: AssetAssignmentFilter,
    filterParams: { buttons: ['reset'], debounceMs: 300 },
    cellRenderer: EndpointCountCellRenderer,
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return row.assets?.length ?? 0;
    },
  },
  {
    field: 'endpointManagement',
    colId: 'endpointManagement',
    headerName: 'Endpoint Mgmt',
    minWidth: getMinWidthForColumn('endpointManagement'),
    sortable: false,
    filter: EndpointManagementCoverageFilter,
    filterParams: { buttons: ['reset'], debounceMs: 300 },
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: ENDPOINT_MANAGEMENT,
      domainType: assetDomainToTypeMap[ENDPOINT_MANAGEMENT],
      isAssetCell: true,
    },
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return `Providers: ${getEndpointsStatus(row.assets, ENDPOINT_MANAGEMENT, params.context)}`;
    },
    ...domainTooltipExport(ENDPOINT_MANAGEMENT, true),
  },
  {
    field: 'endpointSecurity',
    colId: 'endpointSecurity',
    headerName: 'Endpoint Security',
    minWidth: getMinWidthForColumn('endpointSecurity'),
    sortable: false,
    filter: false,
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: ENDPOINT_SECURITY,
      domainType: assetDomainToTypeMap[ENDPOINT_SECURITY],
      isAssetCell: true,
    },
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return `Providers: ${getEndpointsStatus(row.assets, ENDPOINT_SECURITY, params.context)}`;
    },
    ...domainTooltipExport(ENDPOINT_SECURITY, true),
  },
  {
    field: 'vulnerabilityManagement',
    colId: 'vulnerabilityManagement',
    headerName: 'Vulnerability Mgmt',
    minWidth: getMinWidthForColumn('vulnerabilityManagement'),
    sortable: false,
    filter: VulnerabilityCoverageFilter,
    filterParams: { buttons: ['reset'], debounceMs: 300 },
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: VULNERABILITY_MANAGEMENT,
      domainType: assetDomainToTypeMap[VULNERABILITY_MANAGEMENT],
      isAssetCell: true,
    },
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return `${getEndpointsStatus(row.assets, VULNERABILITY_MANAGEMENT, params.context)}`;
    },
    ...domainTooltipExport(VULNERABILITY_MANAGEMENT, true),
  },
  {
    field: 'reason',
    colId: 'reason',
    headerName: 'Reason',
    hide: true,
    sortable: false,
    filter: false,
    valueGetter: (params: any) => {
      const row: UserCoverageSummary = params.data;
      return getUserRiskReason(row, params.context);
    },
  },
];

export default usersInsightsColumns;
